import React from 'react';
import {
  Box,
  Heading,
  Flex,
  Spinner,
  Text,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Icon,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  useToast,
} from '@chakra-ui/react';
import { FiUsers, FiDollarSign, FiBookOpen, FiUserPlus, FiPlus } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import xhr from 'xhr';
import dayjs from 'dayjs';
import useAuth from 'hooks/useAuth';

function StatCard({ icon, label, value, color, helpText }) {
  return (
    <Box bg="white" p={6} borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.100">
      <Flex align="center" gap={4}>
        <Flex
          w="48px"
          h="48px"
          borderRadius="lg"
          bg={`${color}.50`}
          align="center"
          justify="center"
          flexShrink={0}
        >
          <Icon as={icon} boxSize={5} color={`${color}.500`} />
        </Flex>
        <Stat>
          <StatLabel color="gray.500" fontSize="sm">{label}</StatLabel>
          <StatNumber fontSize="2xl">{value}</StatNumber>
          {helpText && <StatHelpText mb={0}>{helpText}</StatHelpText>}
        </Stat>
      </Flex>
    </Box>
  );
}

function QuickAction({ icon, label, description, to, color }) {
  return (
    <Box
      as={Link}
      to={to}
      bg="white"
      border="1px solid"
      borderColor="gray.100"
      borderRadius="xl"
      p={5}
      maxW="33%"
      minW="200px"
      _hover={{ borderColor: `${color}.300`, boxShadow: 'md', transform: 'translateY(-2px)' }}
      transition="all 0.2s"
      textDecoration="none"
      cursor="pointer"
    >
      <Flex align="center" gap={4}>
        <Flex
          w="44px"
          h="44px"
          borderRadius="lg"
          bg={`${color}.500`}
          align="center"
          justify="center"
          flexShrink={0}
        >
          <Icon as={icon} boxSize={5} color="white" />
        </Flex>
        <Box>
          <Text fontSize="sm" fontWeight="semibold" color="gray.800">{label}</Text>
          <Text fontSize="xs" color="gray.500">{description}</Text>
        </Box>
      </Flex>
    </Box>
  );
}

export default function SchoolDashboard() {
  const toast = useToast();
  const { user } = useAuth();

  const [loading, setLoading] = React.useState(true);
  const [stats, setStats] = React.useState({
    students: 0,
    payments: 0,
    programs: 0,
  });
  const [recentStudents, setRecentStudents] = React.useState([]);
  const [recentPayments, setRecentPayments] = React.useState([]);
  const [schoolName, setSchoolName] = React.useState('');

  React.useEffect(() => {
    const init = async () => {
      try {
        const userRes = await xhr('/users/me?populate=school');
        const schoolId = userRes.data?.school?.id;
        setSchoolName(userRes.data?.school?.name || '');

        if (!schoolId) {
          setLoading(false);
          return;
        }

        const [studentsRes, paymentsRes, programsRes, recentStudentsRes, recentPaymentsRes] =
          await Promise.all([
            xhr(
              `/students?filters[schoolFrom][id][$eq]=${schoolId}&filters[user][id][$notNull]=true&pagination[pageSize]=1&pagination[withCount]=true`
            ),
            xhr(
              `/payment-schools?filters[school][id][$eq]=${schoolId}&pagination[pageSize]=1&pagination[withCount]=true`
            ),
            xhr(
              `/school-programs?filters[school][id][$eq]=${schoolId}&filters[active][$eq]=true&pagination[pageSize]=1&pagination[withCount]=true`
            ).catch(() => ({ data: { meta: { pagination: { total: 0 } } } })),
            xhr(
              `/students?filters[schoolFrom][id][$eq]=${schoolId}&filters[user][id][$notNull]=true&populate=user&sort[0]=id:desc&pagination[pageSize]=10`
            ),
            xhr(
              `/payment-schools?filters[school][id][$eq]=${schoolId}&populate=student&sort[0]=payment_date:desc&pagination[pageSize]=10`
            ),
          ]);

        setStats({
          students: studentsRes.data.meta?.pagination?.total || 0,
          payments: paymentsRes.data.meta?.pagination?.total || 0,
          programs: programsRes.data.meta?.pagination?.total || 0,
        });

        setRecentStudents(recentStudentsRes.data?.data || []);
        setRecentPayments(recentPaymentsRes.data?.data || []);
      } catch (error) {
        console.error('Error loading dashboard:', error);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="lg" />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box mb={8}>
        <Heading size="lg" mb={1}>
          Bienvenido, {user?.first_name || 'Administrador'}
        </Heading>
        <Text color="gray.500">
          {schoolName} &middot; {dayjs().format('dddd, D [de] MMMM YYYY')}
        </Text>
      </Box>

      {/* Stats */}
      <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={5} mb={8}>
        <StatCard
          icon={FiUsers}
          label="Estudiantes"
          value={stats.students}
          color="blue"
        />
        <StatCard
          icon={FiDollarSign}
          label="Pagos registrados"
          value={stats.payments}
          color="green"
        />
        <StatCard
          icon={FiBookOpen}
          label="Programas activos"
          value={stats.programs}
          color="purple"
        />
      </SimpleGrid>

      {/* Recent Activity */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        {/* Recent Students */}
        <Box bg="white" borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.100" overflow="hidden">
          <Box p={5} pb={3}>
            <Heading size="sm">Estudiantes recientes</Heading>
          </Box>
          <TableContainer>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Nombre</Th>
                  <Th>Email</Th>
                </Tr>
              </Thead>
              <Tbody>
                {recentStudents
                  .filter((s) => {
                    const attrs = s.attributes || s;
                    const u = attrs.user?.data?.attributes || attrs.user;
                    return !!u?.email || !!u?.first_name;
                  })
                  .map((s) => {
                  const attrs = s.attributes || s;
                  const u = attrs.user?.data?.attributes || attrs.user;
                  return (
                    <Tr key={s.id}>
                      <Td fontSize="sm">
                        {u?.first_name || ''} {u?.last_name || ''}
                      </Td>
                      <Td fontSize="sm" color="gray.600">{u?.email || ''}</Td>
                    </Tr>
                  );
                })}
                {recentStudents.length === 0 && (
                  <Tr>
                    <Td colSpan={2} textAlign="center" py={6} color="gray.400">
                      Sin estudiantes registrados
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>

        {/* Recent Payments */}
        <Box bg="white" borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.100" overflow="hidden">
          <Box p={5} pb={3}>
            <Heading size="sm">Pagos recientes</Heading>
          </Box>
          <TableContainer>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Fecha</Th>
                  <Th>Estudiante</Th>
                  <Th isNumeric>Monto</Th>
                  <Th>Estado</Th>
                </Tr>
              </Thead>
              <Tbody>
                {recentPayments.map((p) => {
                  const attrs = p.attributes || p;
                  const student = attrs.student?.data?.attributes || attrs.student;
                  const studentUser = student?.user?.data?.attributes || student?.user;
                  return (
                    <Tr key={p.id}>
                      <Td fontSize="sm">
                        {attrs.payment_date ? dayjs(attrs.payment_date).format('DD/MM/YY') : '-'}
                      </Td>
                      <Td fontSize="sm">
                        {studentUser?.first_name || ''} {studentUser?.last_name || ''}
                      </Td>
                      <Td isNumeric fontSize="sm" fontWeight="medium">
                        ${Number(attrs.amount || 0).toLocaleString()}
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={attrs.status === 'paid' ? 'green' : attrs.status === 'pending' ? 'yellow' : 'red'}
                          fontSize="xs"
                        >
                          {attrs.status === 'paid' ? 'Pagado' : attrs.status === 'pending' ? 'Pendiente' : 'Cancelado'}
                        </Badge>
                      </Td>
                    </Tr>
                  );
                })}
                {recentPayments.length === 0 && (
                  <Tr>
                    <Td colSpan={4} textAlign="center" py={6} color="gray.400">
                      Sin pagos registrados
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      </SimpleGrid>
    </Box>
  );
}
